const IRREGULAR_WHITESPACE = /\u00A0/g

export type FormatPriceOptions = {
  currencySymbol?: string
  fractionDigits?: number
  priceFormat?: string
}

const formatWithCustomSymbol = ({
  value,
  locale,
  currencySymbol,
  fractionDigits,
  priceFormat
}: {
  value: number
  locale: string | undefined
  currencySymbol: string
  fractionDigits: number
  priceFormat: string
}): string => {
  const numberFormatOptions: Intl.NumberFormatOptions =
    fractionDigits >= 0
      ? {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits
        }
      : {}

  const formattedNumber = new Intl.NumberFormat(
    locale || 'en-US',
    numberFormatOptions
  )
    .format(value)
    .replace(IRREGULAR_WHITESPACE, ' ')

  return priceFormat
    .replace('$currency_symbol$', currencySymbol)
    .replace('$price$', formattedNumber)
}

const formatWithIntlCurrency = ({
  value,
  currency,
  locale,
  fractionDigits
}: {
  value: number
  currency: string
  locale: string | undefined
  fractionDigits: number
}): string => {
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency
  }

  if (fractionDigits >= 0) {
    formatOptions.minimumFractionDigits = fractionDigits
    formatOptions.maximumFractionDigits = fractionDigits
  }

  return new Intl.NumberFormat(locale || 'en-US', formatOptions)
    .format(value)
    .replace(IRREGULAR_WHITESPACE, ' ')
}

export interface FormatPriceParams {
  value?: number | null
  currency?: string
  locale?: string
  options?: FormatPriceOptions
}

export const formatPrice = ({
  value,
  currency,
  locale,
  options
}: FormatPriceParams) => {
  if (value == null || !currency) {
    return undefined
  }

  const {
    currencySymbol = '',
    fractionDigits = -1,
    priceFormat = '$currency_symbol$ $price$'
  } = options || {}

  try {
    if (currencySymbol) {
      return formatWithCustomSymbol({
        value,
        locale,
        currencySymbol,
        fractionDigits,
        priceFormat
      })
    }

    return formatWithIntlCurrency({ value, currency, locale, fractionDigits })
  } catch {
    return undefined
  }
}
